import { Heading, VStack, FormControl, FormLabel, Input, Button, Text, useToast, HStack, Link } from '@chakra-ui/react';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { useSendOTP } from '../hooks/useSendOTP';
import { useVerifyOTP } from '../hooks/useVerifyOTP';
import { MIN_PASSWORD_LENGTH, useUpdatePassword } from '../hooks/useUpdatePassword';
import TermsOfService from '../modals/TermsOfService';
import PrivacyPolicy from '../modals/PrivacyPolicy';

const inputStyles = {
    bg: 'gray.50',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    h: '44px',
    _hover: { borderColor: 'gray.300' },
    _focus: { borderColor: 'midnight', bg: 'white', boxShadow: 'none' },
} as const;

const ctaStyles = {
    bg: 'midnight',
    color: 'white',
    w: '100%',
    h: '44px',
    borderRadius: 'lg',
    fontWeight: 'semibold',
    _hover: { bg: 'gray.800' },
    _active: { bg: 'gray.900' },
} as const;

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [canEnterOTP, setCanEnterOTP] = useState(false);
    const [otp, setOtp] = useState('');
    const [resetToken, setResetToken] = useState<string | null>(null);
    const verified = resetToken !== null;
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const navigate = useNavigate();
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const sendOTP = useSendOTP();
    const verifyOTP = useVerifyOTP();
    const updatePassword = useUpdatePassword();
    const toast = useToast();

    const handleSubmitEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        sendOTP.mutateAsync({ email, accountNeeded: true })
            .then(() => {
                toast({
                    title: "Request Submitted",
                    description: "If an account exists for this email, an OTP has been sent",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                setCanEnterOTP(true);
            })
            .catch((error) => {
                toast({
                    title: "Request Failed",
                    description: error?.response?.data?.error ?? "Could not process your request. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            });
    };

    const handleSubmitOTP = async (e: React.FormEvent) => {
        e.preventDefault();

        verifyOTP.mutateAsync({ email, otp })
            .then((res) => {
                toast({
                    title: "OTP Verified Successfully",
                    description: "You can now proceed to reset your password.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                // An API older than the reset-token change returns no body; it doesn't check the token either.
                setResetToken(res.data?.resetToken ?? '');
            })
            .catch(() => {
                toast({
                    title: "OTP Verification Failed",
                    description: "The OTP entered is incorrect or has expired. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            });
    };

    const handleSubmitNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) {
            toast({
                title: "Passwords Do Not Match",
                description: "Please ensure both password fields are identical.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            toast({
                title: "Password Too Short",
                description: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (resetToken === null) return;

        updatePassword.mutateAsync({ email, password: newPassword, resetToken })
            .then(() => navigate("/login"))
            .catch(() => toast({
                title: "Something went wrong",
                description: "Please try again",
                status: "error",
                duration: 4000,
                isClosable: true,
            }))
    };

    const renderFormContent = () => {
        if (verified) {
            return (
                <VStack spacing={6} as="form" onSubmit={handleSubmitNewPassword} align="stretch">
                    <VStack spacing={1} align="stretch">
                        <Heading fontFamily="heading" fontSize="2xl" fontWeight="bold" color="gray.900">
                            Set new password
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                            Enter your new password below for {email}.
                        </Text>
                    </VStack>
                    <FormControl isRequired>
                        <FormLabel fontSize="sm" color="gray.700" mb={1.5}>New password</FormLabel>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter your new password"
                            {...inputStyles}
                        />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel fontSize="sm" color="gray.700" mb={1.5}>Confirm new password</FormLabel>
                        <Input
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            placeholder="Confirm your new password"
                            {...inputStyles}
                        />
                    </FormControl>
                    <Button
                        type="submit"
                        isLoading={updatePassword.isPending}
                        isDisabled={updatePassword.isPending}
                        {...ctaStyles}
                    >
                        Reset password & log in
                    </Button>
                </VStack>
            );
        }

        return (
            <VStack spacing={6} as="form" onSubmit={canEnterOTP ? handleSubmitOTP : handleSubmitEmail} align="stretch">
                <VStack spacing={1} align="stretch">
                    <Heading fontFamily="heading" fontSize="2xl" fontWeight="bold" color="gray.900">
                        {canEnterOTP ? 'Enter verification code' : 'Reset your password'}
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                        {canEnterOTP
                            ? `We sent a verification code to ${email}. Enter it below.`
                            : "No worries — enter your email address and we'll send you a code to verify it."}
                    </Text>
                </VStack>

                <FormControl isRequired>
                    <FormLabel fontSize="sm" color="gray.700" mb={1.5}>Email address</FormLabel>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@shop.com"
                        isDisabled={sendOTP.isPending || canEnterOTP}
                        {...inputStyles}
                    />
                </FormControl>

                {canEnterOTP && (
                    <FormControl isRequired>
                        <FormLabel fontSize="sm" color="gray.700" mb={1.5}>Verification code (OTP)</FormLabel>
                        <Input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            placeholder="Enter the 6-digit code"
                            maxLength={6}
                            isDisabled={verifyOTP.isPending}
                            {...inputStyles}
                        />
                    </FormControl>
                )}

                <Button
                    type="submit"
                    isLoading={sendOTP.isPending || verifyOTP.isPending}
                    {...ctaStyles}
                >
                    {canEnterOTP ? 'Verify code' : 'Get code'}
                </Button>

                <Text fontSize="sm" textAlign="center" color="gray.500">
                    {canEnterOTP ? (
                        <Text
                            as="button"
                            type="button"
                            color="brand.fg"
                            fontWeight="medium"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => {
                                setCanEnterOTP(false);
                                setOtp('');
                                setEmail('');
                            }}
                        >
                            Entered the wrong email?
                        </Text>
                    ) : (
                        <>
                            Remember your password?{' '}
                            <Text as={RouterLink} to="/login" color="brand.fg" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
                                Back to log in
                            </Text>
                        </>
                    )}
                </Text>
            </VStack>
        );
    };

    return (
        <>
            <AuthLayout
                footer={
                    <HStack spacing={4} color="gray.400" fontSize="xs">
                        <Link onClick={() => setIsTermsModalOpen(true)} _hover={{ textDecoration: 'underline' }} cursor="pointer">
                            Terms of service
                        </Link>
                        <Link onClick={() => setIsPrivacyModalOpen(true)} _hover={{ textDecoration: 'underline' }} cursor="pointer">
                            Privacy policy
                        </Link>
                        <Link href="mailto:contact@yaxle.com" _hover={{ textDecoration: 'underline' }}>
                            contact@yaxle.com
                        </Link>
                    </HStack>
                }
            >
                {renderFormContent()}
            </AuthLayout>
            <TermsOfService isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
            <PrivacyPolicy isOpen={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
        </>
    );
};

export default ForgotPassword;
